import { useTranslation } from 'react-i18next';
import { Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import s from './TasksDashboard.module.scss';

import type { FC } from 'react';

interface ITasksHeaderProps {
  search: string;
  onSearchChange: (value: string) => void;
  podFilter?: string;
  onPodFilterChange: (value: string | undefined) => void;
  priorityFilter?: string;
  onPriorityFilterChange: (value: string | undefined) => void;
  reporterFilter?: string;
  onReporterFilterChange: (value: string | undefined) => void;
  totalResults?: number;
}

const TasksHeader: FC<ITasksHeaderProps> = ({
  search,
  onSearchChange,
  podFilter,
  onPodFilterChange,
  priorityFilter,
  onPriorityFilterChange,
  reporterFilter,
  onReporterFilterChange,
  totalResults,
}) => {
  const { t } = useTranslation();

  const podOptions = [
    { value: '', label: t('tasksDashboard.filters.allPod') },
    { value: 'primary', label: t('tasksDashboard.filters.primary') },
    { value: 'secondary', label: t('tasksDashboard.filters.secondary') },
    { value: 'tertiary', label: t('tasksDashboard.filters.tertiary') },
    { value: 'outside_pod', label: t('tasksDashboard.filters.outsidePod') },
  ];

  const priorityOptions = [
    { value: '', label: t('tasksDashboard.filters.allPriority') },
    { value: 'high', label: t('tasksDashboard.filters.high') },
    { value: 'medium', label: t('tasksDashboard.filters.medium') },
    { value: 'low', label: t('tasksDashboard.filters.low') },
  ];

  const reporterOptions = [
    { value: '', label: t('tasksDashboard.filters.allReporter') },
    { value: 'me', label: t('tasksDashboard.filters.byMe') },
  ];

  const toFilterValue = (val: string): string | undefined =>
    val === '' ? undefined : val;

  return (
    <div className={s.header}>
      <div className={s['header-row']}>
        <h1 className={s.title}>{t('tasksDashboard.title')}</h1>
        <div className={s['filters-row']}>
          <Input
            className={s['search-input']}
            placeholder={t('tasksDashboard.search.placeholder')}
            suffix={<SearchOutlined />}
            allowClear
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label={t('tasksDashboard.search.ariaLabel')}
            data-cy="tasks-search-input"
          />
          <label className={s['sr-only']} id="pod-filter-label">
            {t('tasksDashboard.filters.podLabel')}
          </label>
          <Select
            className={s['filter-select']}
            value={podFilter ?? ''}
            onChange={(val) => onPodFilterChange(toFilterValue(val))}
            options={podOptions}
            aria-labelledby="pod-filter-label"
            data-cy="tasks-pod-filter"
          />
          <label className={s['sr-only']} id="priority-filter-label">
            {t('tasksDashboard.filters.priorityLabel')}
          </label>
          <Select
            className={s['filter-select']}
            value={priorityFilter ?? ''}
            onChange={(val) => onPriorityFilterChange(toFilterValue(val))}
            options={priorityOptions}
            aria-labelledby="priority-filter-label"
            data-cy="tasks-priority-filter"
          />
          <label className={s['sr-only']} id="reporter-filter-label">
            {t('tasksDashboard.filters.reporterLabel')}
          </label>
          <Select
            className={s['filter-select']}
            value={reporterFilter ?? ''}
            onChange={(val) => onReporterFilterChange(toFilterValue(val))}
            options={reporterOptions}
            aria-labelledby="reporter-filter-label"
            data-cy="tasks-reporter-filter"
          />
        </div>
      </div>
      <div
        aria-live="polite"
        className={s['sr-only']}
        data-cy="filter-announcement"
      >
        {totalResults !== undefined &&
          `${totalResults} task${totalResults !== 1 ? 's' : ''} found`}
      </div>
    </div>
  );
};

export default TasksHeader;
